import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { IoAdd, IoClose, IoCalendarOutline, IoChevronDown, IoChevronBack, IoCreateOutline, IoTrashOutline } from 'react-icons/io5';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, where, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';

const COLORS = {
  primary: '#4A6741',
  active: '#E85830',
  header: '#2C3640',
  background: '#F4F3F8',
};

const WorkHistoryPage = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userData?.uid) return;
    const loadWorks = async () => {
      try {
        const q = query(
          collection(db, 'constructions'),
          where('uid', '==', userData.uid),
          orderBy('createdAt', 'desc')
        );
        const snap = await getDocs(q);
        setWorks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('시공내역 로드 실패:', err);
      } finally {
        setLoading(false);
      }
    };
    loadWorks();
  }, [userData?.uid]);
  const [expandedId, setExpandedId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: '', amount: '', address: '', startDate: '', endDate: '' });

  const totalAmount = works.reduce((sum, w) => sum + w.amount, 0);

  const resetForm = () => {
    setForm({ title: '', amount: '', address: '', startDate: '', endDate: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleAdd = async () => {
    if (!form.title || !form.amount || !userData?.uid) return;
    const data = {
      title: form.title,
      amount: Number(form.amount),
      address: form.address,
      startDate: form.startDate,
      endDate: form.endDate,
    };
    try {
      if (editingId) {
        await updateDoc(doc(db, 'constructions', editingId), data);
        setWorks(prev => prev.map(w => w.id === editingId ? { ...w, ...data } : w));
      } else {
        const docRef = await addDoc(collection(db, 'constructions'), {
          ...data,
          uid: userData.uid,
          company: userData.companyName || '',
          status: 'in_progress',
          createdAt: serverTimestamp(),
        });
        setWorks(prev => [{ id: docRef.id, ...data, uid: userData.uid, company: userData.companyName || '', status: 'in_progress' }, ...prev]);
      }
      resetForm();
    } catch (err) {
      console.error('시공내역 저장 실패:', err);
    }
  };

  const handleEdit = (e, w) => {
    e.stopPropagation();
    setEditingId(w.id);
    setForm({ title: w.title, amount: String(w.amount), address: w.address || '', startDate: w.startDate || '', endDate: w.endDate || '' });
    setShowForm(true);
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (window.confirm('이 시공내역을 삭제하시겠습니까?')) {
      try {
        await deleteDoc(doc(db, 'constructions', id));
        setWorks(prev => prev.filter(w => w.id !== id));
        if (expandedId === id) setExpandedId(null);
      } catch (err) {
        console.error('시공내역 삭제 실패:', err);
      }
    }
  };

  return (
    <Container>
      <Header>
        <BackBtn onClick={() => navigate(-1)}><IoChevronBack size={24} /></BackBtn>
        <HeaderTitle>시공내역</HeaderTitle>
        <HeaderRight />
      </Header>

      <SummaryCard>
        <SummaryRow>
          <SummaryLabel>총 시공건수</SummaryLabel>
          <SummaryValue>{works.length}건</SummaryValue>
        </SummaryRow>
        <SummaryRow>
          <SummaryLabel>총 시공금액</SummaryLabel>
          <SummaryTotal>{totalAmount.toLocaleString()}원</SummaryTotal>
        </SummaryRow>
      </SummaryCard>

      <ListHeader>
        <ListTitle>내역 목록</ListTitle>
        <AddBtn onClick={() => setShowForm(true)}>
          <IoAdd size={16} />
          등록
        </AddBtn>
      </ListHeader>

      {works.length === 0 ? (
        <EmptyState>등록된 시공내역이 없습니다</EmptyState>
      ) : (
        works.map(w => {
          const isOpen = expandedId === w.id;
          return (
            <WorkCard key={w.id} onClick={() => setExpandedId(isOpen ? null : w.id)}>
              <CardTop>
                <CardTitle>{w.title}</CardTitle>
                <CardRight>
                  <CardAmount>{w.amount.toLocaleString()}원</CardAmount>
                  <ChevronIcon $open={isOpen}><IoChevronDown size={16} /></ChevronIcon>
                </CardRight>
              </CardTop>
              {isOpen && (
                <CardDetail>
                  {w.address && (
                    <DetailRow>
                      <DetailLabel>현장주소</DetailLabel>
                      <DetailValue>{w.address}</DetailValue>
                    </DetailRow>
                  )}
                  {(w.startDate || w.endDate) && (
                    <DetailRow>
                      <DetailLabel>시공기간</DetailLabel>
                      <DetailValue>
                        <IoCalendarOutline size={12} style={{ marginRight: 4 }} />
                        {w.startDate} ~ {w.endDate}
                      </DetailValue>
                    </DetailRow>
                  )}
                  <ActionRow>
                    <ActionBtn onClick={(e) => handleEdit(e, w)}>
                      <IoCreateOutline size={14} />
                      수정
                    </ActionBtn>
                    <ActionBtn $danger onClick={(e) => handleDelete(e, w.id)}>
                      <IoTrashOutline size={14} />
                      삭제
                    </ActionBtn>
                  </ActionRow>
                </CardDetail>
              )}
            </WorkCard>
          );
        })
      )}

      {showForm && (
        <FormOverlay onClick={resetForm}>
          <FormSheet onClick={e => e.stopPropagation()}>
            <SheetHandle />
            <FormHeader>
              <FormTitle>{editingId ? '시공내역 수정' : '시공내역 등록'}</FormTitle>
              <FormCloseBtn onClick={resetForm}><IoClose size={20} /></FormCloseBtn>
            </FormHeader>

            <FormLabel>시공내역 *</FormLabel>
            <FormInput
              placeholder="시공 내용을 입력하세요"
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
            />

            <FormLabel>매출금액 *</FormLabel>
            <FormInput
              placeholder="금액 입력 (숫자만)"
              value={form.amount}
              onChange={e => setForm({ ...form, amount: e.target.value.replace(/\D/g, '') })}
              inputMode="numeric"
            />
            {form.amount && (
              <AmountPreview>{Number(form.amount).toLocaleString()}원</AmountPreview>
            )}

            <FormLabel>현장주소</FormLabel>
            <FormInput
              placeholder="예: 서울 강남구 논현동"
              value={form.address}
              onChange={e => setForm({ ...form, address: e.target.value })}
            />

            <FormLabel>시공기간</FormLabel>
            <DateRow>
              <DateInput
                type="date"
                value={form.startDate}
                onChange={e => setForm({ ...form, startDate: e.target.value })}
              />
              <DateSep>~</DateSep>
              <DateInput
                type="date"
                value={form.endDate}
                onChange={e => setForm({ ...form, endDate: e.target.value })}
              />
            </DateRow>

            <FormSubmitBtn onClick={handleAdd} disabled={!form.title || !form.amount}>
              {editingId ? '수정하기' : '등록하기'}
            </FormSubmitBtn>
          </FormSheet>
        </FormOverlay>
      )}
    </Container>
  );
};

export default WorkHistoryPage;

const Container = styled.div`
  min-height: 100vh;
  background: ${COLORS.background};
  padding-bottom: 100px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #F0F0EC;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 10;
`;

const BackBtn = styled.button`
  display: flex;
  align-items: center;
  color: #1E2A32;
  padding: 4px;
`;

const HeaderTitle = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #1E2A32;
`;

const HeaderRight = styled.div`width: 32px;`;

const SummaryCard = styled.div`
  margin: 12px 16px 0;
  background: white;
  border-radius: 16px;
  padding: 18px 20px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  &:not(:last-child) { margin-bottom: 8px; }
`;

const SummaryLabel = styled.span`
  font-size: 13px;
  color: #8C9AA4;
`;

const SummaryValue = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: ${COLORS.header};
`;

const SummaryTotal = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: ${COLORS.active};
`;

const ListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 16px 8px;
`;

const ListTitle = styled.p`
  font-size: 15px;
  font-weight: 700;
  color: ${COLORS.header};
`;

const AddBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 14px;
  border-radius: 8px;
  background: ${COLORS.header};
  color: white;
  font-size: 13px;
  font-weight: 600;
`;

const EmptyState = styled.p`
  font-size: 13px;
  color: #bbb;
  text-align: center;
  padding: 60px 0;
`;

const WorkCard = styled.div`
  margin: 0 16px 6px;
  background: white;
  border-radius: 12px;
  padding: 14px 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  cursor: pointer;
`;

const CardTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CardTitle = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: #1E2A32;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CardRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin-left: 12px;
`;

const CardAmount = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${COLORS.primary};
`;

const ChevronIcon = styled.div`
  color: #8C9AA4;
  display: flex;
  transition: transform 0.2s;
  transform: ${({ $open }) => $open ? 'rotate(180deg)' : 'rotate(0)'};
`;

const CardDetail = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #F0F0EE;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  &:not(:last-child) { margin-bottom: 8px; }
`;

const DetailLabel = styled.span`
  font-size: 12px;
  color: #8C9AA4;
`;

const DetailValue = styled.span`
  font-size: 13px;
  color: #1E2A32;
  display: flex;
  align-items: center;
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #F0F0EE;
`;

const ActionBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  background: ${({ $danger }) => $danger ? '#FFF0EE' : '#F4F3F8'};
  color: ${({ $danger }) => $danger ? '#EF5350' : '#555'};
`;

/* 바텀시트 */
const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

const FormOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  z-index: 1000;
  display: flex;
  align-items: flex-end;
`;

const FormSheet = styled.div`
  width: 100%;
  background: white;
  border-radius: 20px 20px 0 0;
  padding: 0 24px 34px;
  animation: ${slideUp} 0.25s ease;
`;

const SheetHandle = styled.div`
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: #DDD;
  margin: 10px auto 0;
`;

const FormHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0 8px;
`;

const FormTitle = styled.p`
  font-size: 16px;
  font-weight: 700;
  color: #1E2A32;
`;

const FormCloseBtn = styled.button`
  color: #8C9AA4;
  display: flex;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 13px;
  color: #666;
  margin: 14px 0 6px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #E0E0E0;
  border-radius: 10px;
  font-size: 14px;
  &:focus { border-color: ${COLORS.primary}; outline: none; }
`;

const AmountPreview = styled.p`
  font-size: 12px;
  color: ${COLORS.primary};
  font-weight: 600;
  margin-top: 4px;
  padding-left: 2px;
`;

const DateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DateInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 12px 10px;
  border: 1px solid #E0E0E0;
  border-radius: 10px;
  font-size: 13px;
  color: #333;
  box-sizing: border-box;
  &:focus { border-color: ${COLORS.primary}; outline: none; }
`;

const DateSep = styled.span`
  font-size: 14px;
  color: #999;
`;

const FormSubmitBtn = styled.button`
  width: 100%;
  padding: 14px;
  margin-top: 20px;
  border-radius: 12px;
  background: ${({ disabled }) => disabled ? '#D0D0D0' : COLORS.header};
  color: white;
  font-size: 14px;
  font-weight: 600;
  pointer-events: ${({ disabled }) => disabled ? 'none' : 'auto'};
`;
